/**
 * PATH: packages/ui/src/kanban/components/card-detail/EmailSearchInline.tsx
 * FILE_ID: UI-KANBAN-CMP-EMAIL-SEARCH-INLINE-001
 * PURPOSE: In-place email search for the card drawer's Email tab.
 *          Debounced input → server-side search via the SAME
 *          adapter.listInboxEmails path the allocation rail uses →
 *          actionable result rows ("Link" attaches the email to THIS
 *          card via api.linkEmailToCard, no navigation).
 * WHY:     ARC-1 §4.3 / MR4. Client 2026-06-30: "email search in drawer
 *          glitchy with the same redirect and half-loaded architecture."
 *          The previous affordance was a deep-link anchor into the Email
 *          Hub search page — replying/searching bounced the operator off
 *          the board. Search now happens inside the drawer with proper
 *          debounce + loading/empty/error states, and a result can be
 *          linked to the card in one click (the thread then renders in
 *          place — the operator never leaves the card).
 * FLOW:
 *   ┌───────────┐ 250ms ┌──────────────────────┐  Link  ┌──────────────┐
 *   │ input     │ ────▶ │ adapter.listInbox    │ ─────▶ │ api.linkEmail│
 *   │ (2+ chars)│       │ Emails({search})     │        │ ToCard → thread│
 *   └───────────┘       └──────────────────────┘        └──────────────┘
 * DEPENDENCIES:
 *   - adapter.listInboxEmails: server-side subject/sender search.
 *   - api.linkEmailToCard: idempotent attach (POST /cards/<id>/link-email/).
 * RELATED:
 *   - drawer-sections/EmailTabPane.tsx: mounts this in the empty state.
 *   - EmailLinkEmptyState.tsx: the surrounding empty-state copy.
 *   - email-rail/useEmailAllocationRail.ts: same debounce (250ms) + search.
 */
import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link2, Loader2, MailSearch, RotateCcw } from 'lucide-react'

import type { KanbanApi, KanbanEmailAdapter } from '../../api'

/** Same debounce the email rail + header card search use. */
const SEARCH_DEBOUNCE_MS = 250
/** Don't hit the server for 1-char queries — too broad to be useful. */
const MIN_QUERY_LEN = 2
const RESULT_LIMIT = 20

export interface EmailSearchInlineProps {
  /** Card the linked email should attach to. */
  cardId: string
  adapter: KanbanEmailAdapter
  api: KanbanApi
  /** Fired after a successful link so the drawer invalidates its card /
   *  linked-email queries and the thread renders in place. */
  onLinked?: () => void
}

export function EmailSearchInline({
  cardId,
  adapter,
  api,
  onLinked,
}: EmailSearchInlineProps) {
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [linkingId, setLinkingId] = useState<string | null>(null)
  const [linkError, setLinkError] = useState<string | null>(null)

  useEffect(() => {
    const t = window.setTimeout(
      () => setDebounced(search.trim()),
      SEARCH_DEBOUNCE_MS,
    )
    return () => window.clearTimeout(t)
  }, [search])

  const active = debounced.length >= MIN_QUERY_LEN
  const searchQ = useQuery({
    queryKey: ['kanban', 'drawer-email-search', debounced],
    queryFn: async () => {
      if (!adapter.listInboxEmails) return []
      return adapter.listInboxEmails({ search: debounced, limit: RESULT_LIMIT })
    },
    enabled: active,
    staleTime: 10_000,
  })

  const link = async (emailId: string) => {
    if (!api.linkEmailToCard || linkingId) return
    setLinkingId(emailId)
    setLinkError(null)
    try {
      await api.linkEmailToCard({ cardId, emailId })
      onLinked?.()
    } catch (err) {
      setLinkError(
        err instanceof Error ? err.message : 'Could not link this email',
      )
    } finally {
      setLinkingId(null)
    }
  }

  // `isFetching && !data` = first fetch for this term; background
  // refreshes keep the current rows on screen (no skeleton flash).
  const showLoading = active && searchQ.isLoading

  return (
    <div
      data-testid="email-search-inline"
      className="w-full max-w-[360px] text-left"
    >
      <label className="relative block">
        <MailSearch
          className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400"
          aria-hidden
        />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search emails to link…"
          aria-label="Search emails to link to this card"
          data-testid="email-search-inline-input"
          className="w-full rounded-md border border-neutral-200 bg-white py-1.5 pl-8 pr-2.5 text-[12px] text-neutral-800 placeholder:text-neutral-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
        />
      </label>

      {active && (
        <div className="mt-1.5 overflow-hidden rounded-md border border-neutral-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
          {showLoading && (
            <p
              data-testid="email-search-inline-loading"
              className="flex items-center gap-1.5 px-3 py-2 text-[11.5px] text-neutral-500"
            >
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
              Searching “{debounced}”…
            </p>
          )}
          {searchQ.isError && (
            <div className="flex items-center justify-between gap-2 px-3 py-2">
              <p className="text-[11.5px] text-red-600 dark:text-red-400">
                Search failed —{' '}
                {searchQ.error instanceof Error
                  ? searchQ.error.message
                  : 'try again'}
              </p>
              <button
                type="button"
                onClick={() => void searchQ.refetch()}
                className="inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-neutral-600 hover:bg-neutral-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
              >
                <RotateCcw className="h-3 w-3" aria-hidden /> Retry
              </button>
            </div>
          )}
          {searchQ.isSuccess && searchQ.data.length === 0 && (
            <p className="px-3 py-2 text-[11.5px] text-neutral-500">
              No emails match “{debounced}”.
            </p>
          )}
          {searchQ.isSuccess &&
            searchQ.data.map((row) => (
              <div
                key={row.id}
                data-testid={`email-search-inline-row-${row.id}`}
                className="flex items-center gap-2 border-t border-neutral-100 px-3 py-1.5 first:border-t-0 dark:border-zinc-800"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[12px] font-medium text-neutral-800 dark:text-zinc-100">
                    {row.subject || '(no subject)'}
                  </p>
                  <p className="truncate text-[10.5px] text-neutral-500">
                    {row.from}
                    {row.received_at
                      ? ` · ${new Date(row.received_at).toLocaleDateString()}`
                      : ''}
                  </p>
                </div>
                <button
                  type="button"
                  data-testid={`email-search-inline-link-${row.id}`}
                  disabled={!!linkingId}
                  onClick={() => void link(row.id)}
                  className="inline-flex shrink-0 items-center gap-1 rounded-md bg-blue-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {linkingId === row.id ? (
                    <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                  ) : (
                    <Link2 className="h-3 w-3" aria-hidden />
                  )}
                  Link
                </button>
              </div>
            ))}
          {linkError && (
            <p className="border-t border-neutral-100 px-3 py-1.5 text-[11px] text-red-600 dark:border-zinc-800 dark:text-red-400">
              {linkError}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
